import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
  }

  /**
   * Helper: detect if a cart item is a liner
   * @param {{product_title?: string, title?: string, handle?: string, product_type?: string, price?: number, final_price?: number}} item
   * @returns {boolean}
   */
  #isLinerItem(item) {
    const title = (item.product_title || item.title || '').toLowerCase();
    const handle = (item.handle || '').toLowerCase();
    const pType = (item.product_type || '').toLowerCase();
    if (title.includes('liner') || handle.includes('liner') || pType.includes('liner')) return true;
    // Check collections array if present (not always available via cart.js)
    if (Array.isArray(item.collections)) {
      for (const col of item.collections) {
        const ch = (col.handle || col || '').toLowerCase();
        if (ch === 'explore-lip-liners' || ch === 'free-liner-over-5-lipstick') return true;
      }
    }
    // Fallback: liners are priced at or below ₹350 (35000 paise)
    const itemPrice = item.price || item.final_price || 0;
    if (itemPrice > 0 && itemPrice <= 35000) return true;
    return false;
  }

  checkAndRemoveFreeGift() {
    fetch('/cart.js')
      .then((res) => res.json())
      .then((cart) => {
        let totalLipsticks = 0;
        let zeroPriceItemLine = null;

        cart.items.forEach((item, index) => {
          const title = (item.product_title || item.title || '').toLowerCase();
          const itemPrice = item.price || item.final_price || 0;
          const props = item.properties || {};
          const isFreeGiftProp = props['_free_gift'] === 'true' || props['_free_gift'] === true;
          const isBundleProp = !!(props['_bundle_group'] || props['_bundle_id']);

          if ((itemPrice === 0 || isFreeGiftProp) && !(isBundleProp && !isFreeGiftProp)) {
            zeroPriceItemLine = index + 1;
            if (item.quantity > 1) {
              this.updateQuantity({
                line: index + 1,
                quantity: 1,
                action: 'set',
              });
            }
            return;
          }

          const isLiner = this.#isLinerItem(item);
          if (!isLiner) {
            if (title.includes('pack of 2') || title.includes('bundle of 2') || title.includes('2lipstick') || title.includes('2 lipstick')) {
              totalLipsticks += item.quantity * 2;
            } else if (title.includes('pack of 3') || title.includes('bundle of 3')) {
              totalLipsticks += item.quantity * 3;
            } else if (title.includes('pack of 5') || title.includes('bundle of 5')) {
              totalLipsticks += item.quantity * 5;
            } else if (title.includes('pack of 7') || title.includes('bundle of 7')) {
              totalLipsticks += item.quantity * 7;
            } else {
              totalLipsticks += item.quantity;
            }
          }
        });

        if (totalLipsticks < 2 && zeroPriceItemLine !== null) {
          this.updateQuantity({
            line: zeroPriceItemLine,
            quantity: 0,
            action: 'clear',
          });
        }
      })
      .catch((err) => console.error('Free gift validation error:', err));
  }

  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    setTimeout(() => {
      this.checkAndRemoveFreeGift();
    }, 400);

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    const isEmptyCart = rowsToRemove.length === this.refs.cartItemRows.length;
    const template = document.getElementById('empty-cart-template');

    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);
      return;
    }

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();
      if (prefersReducedMotion()) return remove();
      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);
        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });
        this.#updateCartQuantitySelectorButtonStates();
        
        this.checkAndRemoveFreeGift();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    if (quantityInput) quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError && cartItemErrorContainer) {
      cartItemError.textContent = parsedResponseText.errors;
      cartItemErrorContainer.classList.remove('hidden');
    }
  };

  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    // After any external cart update (e.g. product page add-to-cart), check if a liner
    // needs to be converted to a free gift based on current lipstick count
    setTimeout(() => this.checkAndRemoveFreeGift(), 400);
  };

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }

  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}

window.checkAndRemoveFreeGift = function() {
  const cartComp = document.querySelector('cart-items-component');
  if (cartComp && typeof cartComp.checkAndRemoveFreeGift === 'function') {
    cartComp.checkAndRemoveFreeGift();
  }
};